import { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { signInWithGoogle } from '@/utils/auth';
import { trackTwitterLead } from '@/utils/twitterPixel';
import { toast } from 'sonner';

/**
 * Custom hook for handling sign-in with proper state management
 * Prevents multiple clicks, handles already-logged-in users, and provides loading state
 */
export function useSignIn() {
  const [signingIn, setSigningIn] = useState(false);
  const navigate = useNavigate();
  const processingRef = useRef(false); // Use ref to prevent race conditions

  const handleSignIn = useCallback(async () => {
    console.log('handleSignIn called, signingIn:', signingIn, 'processingRef:', processingRef.current);
    
    // Prevent multiple clicks using both state and ref
    if (signingIn || processingRef.current) {
      console.log('Already signing in, ignoring click');
      return;
    }

    // Set processing flag immediately
    processingRef.current = true;

    // Check if user is already logged in with a timeout
    // If the check takes too long, proceed with sign-in anyway
    try {
      console.log('Checking for existing session...');
      const sessionPromise = supabase.auth.getSession();
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Session check timeout')), 2000)
      );
      
      const { data: { session } } = await Promise.race([
        sessionPromise,
        timeoutPromise
      ]) as { data: { session: any } };
      
      if (session?.user) {
        console.log('User already logged in, navigating to dashboard');
        processingRef.current = false; // Reset before navigation
        // Already logged in, just navigate to dashboard
        navigate('/dashboard');
        return;
      }
      console.log('No existing session found');
    } catch (error: any) {
      // If timeout or error, just proceed with sign-in
      if (error?.message === 'Session check timeout') {
        console.warn('Session check timed out, proceeding with sign-in');
      } else {
        console.error('Error checking session:', error);
      }
      // Continue with sign-in attempt
    }

    try {
      console.log('Setting signingIn to true and calling signInWithGoogle...');
      setSigningIn(true);
      
      // Track Lead event when user initiates sign-up (clicks Join with Google or Get Started)
      // This captures the lead before they go to onboarding
      trackTwitterLead({
        status: 'started', // User started the sign-up process
        conversion_id: null, // No conversion ID yet (user hasn't signed up yet)
        email_address: null // No email yet (will be available after OAuth)
      });
      
      // Don't await - signInWithGoogle will redirect, and we don't want to continue execution
      // If redirect fails, it will throw an error which we catch below
      signInWithGoogle().catch((error) => {
        // Only handle errors if redirect didn't happen
        // If redirect happened, this code won't execute
        console.error('Sign in failed:', error);
        setSigningIn(false); // Reset on error so user can try again
        processingRef.current = false; // Reset processing flag
        const errorMessage = error?.message || 'Failed to initiate sign-in. Please try again.';
        toast.error('Sign-in Error', {
          description: errorMessage,
        });
      });
      // Note: signInWithGoogle will redirect, so we won't reach here on success
      // Execution will stop here as the page redirects
    } catch (error: any) {
      // This catch block should rarely execute since we're not awaiting
      // But keep it as a safety net
      console.error('Sign in failed (sync error):', error);
      setSigningIn(false);
      processingRef.current = false;
      const errorMessage = error?.message || 'Failed to initiate sign-in. Please try again.';
      toast.error('Sign-in Error', {
        description: errorMessage,
      });
    }
  }, [signingIn, navigate]);

  return {
    handleSignIn,
    signingIn,
  };
}


